import axios from "axios";
import { getCurrentCustomer, clearCustomer, checkSessionDrift } from "./customerSession";
import { BACKEND_URL, API_BASE } from "./backendUrl";

export { API_BASE };

const client = axios.create({
  baseURL: API_BASE,
  timeout: 12000,
  // 2026-11 — lets the browser send the httpOnly `session_token` cookie
  // (session_store.py) on these requests; required for the 4 endpoints
  // that now resolve identity server-side from that cookie instead of a
  // client-supplied customer_id.
  withCredentials: true,
});

// 2026-11 — these 4 endpoints now require the session cookie (see
// session_store.get_current_customer_id[_optional] on the backend). A 401
// here means the session expired/was revoked — treat it as a logout so the
// existing route guard (App.js) sends the user back to /login, deep-link
// preserved. Scoped to JUST these paths — 401 is used for unrelated
// reasons elsewhere (admin password, Tally/Zoho agent tokens).
const SESSION_GUARDED_PATHS = ["/customer/dashboard-data", "/executive/summary", "/executive/chat", "/ai-insights/generate"];

// Central, app-wide entitlement enforcement signal — one interceptor, not
// a per-page check. The backend (entitlement_middleware.py) is the single
// source of truth; this only relays its 402 so App.js can show the
// upgrade/renewal state immediately, even before the next entitlement poll.
client.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 402 && err.response.data?.detail === "trial_expired") {
      window.dispatchEvent(new CustomEvent("core:entitlement-expired"));
    }
    if (err?.response?.status === 401) {
      const url = err.config?.url || "";
      if (SESSION_GUARDED_PATHS.some((p) => url.includes(p))) {
        clearCustomer();
      }
    }
    return Promise.reject(err);
  }
);

/** Translate any error into a friendly, plain-English message. */
function friendly(err, fallback = "Something went wrong. Please try again.") {
  if (!err) return fallback;
  if (err.code === "ECONNABORTED") return "The server is taking too long to respond. Please try again.";
  if (err.message === "Network Error") return "We can't reach the server right now. Please check your internet connection.";
  if (err.response) {
    const detail = err.response.data && err.response.data.detail;
    if (typeof detail === "string") return detail;
    if (err.response.status >= 500) return "Our service is having a hiccup. Please try again in a moment.";
    if (err.response.status === 404) return "We couldn't find what you were looking for.";
  }
  return fallback;
}

async function get(path, timeoutMs) {
  try {
    const res = await client.get(path, timeoutMs ? { timeout: timeoutMs } : undefined);
    return { ok: true, data: res.data };
  } catch (err) {
    return { ok: false, error: friendly(err) };
  }
}

async function chatCall(path, message, sessionId) {
  try {
    const cid = activeCustomerId();
    // For active customers we send the customer_id with every chat so the AI
    // greets them by name + only references their real numbers.
    const body = { message, session_id: sessionId };
    if (cid) body.customer_id = cid;
    const res = await client.post(path, body, { timeout: 30000 });
    return { ok: true, data: res.data };
  } catch (err) {
    return { ok: false, error: friendly(err, "Sorry, I could not process that right now. Please try again in a moment.") };
  }
}

async function getCall(path) {
  try {
    const cid = activeCustomerId();
    // Auto-attach ?customer_id=... for any active-customer insights call.
    const full = cid
      ? `${path}${path.includes("?") ? "&" : "?"}customer_id=${encodeURIComponent(cid)}`
      : path;
    const res = await client.get(full, { timeout: 25000 });
    return { ok: true, data: res.data };
  } catch (err) {
    return { ok: false, error: friendly(err) };
  }
}

/**
 * Customer id for the logged-in session, or null for visitors.
 *
 * IMPORTANT: this resolves the id whenever a session exists — it is NOT
 * gated on `status === "active"`. The "active" status is an internal
 * admin flag (`/admin/setup-complete`) and has nothing to do with whether
 * a real customer is logged in. Gating on it caused every executive
 * summary / chat / insights request to drop the customer_id for
 * not-yet-"active" customers, which routed authenticated users to the
 * demo/visitor fallback. This must stay identical to the resolution in
 * CustomerDataProvider.jsx (`c?.id || c?.customer_id`) so the Boardroom's
 * "hide demo data" safety check and the KPI/briefing data can never
 * disagree about who the customer is.
 */
function activeCustomerId() {
  try {
    const c = getCurrentCustomer();
    if (c) return c.id || c.customer_id || null;
  } catch (_e) { /* ignore */ }
  return null;
}

export const api = {
  supabaseHealth: () => get("/health/supabase"),
  company: () => get("/company"),
  // 2026-11 — identity now comes from the session cookie (withCredentials
  // above), not a query param; the backend ignores `customer_id` on this
  // route entirely. No `cid` branch needed either way — the cookie, if
  // valid, always wins server-side. checkSessionDrift guards against a
  // stale sessionStorage customer outliving an expired/cleared cookie.
  executiveSummary: async () => {
    const res = await get("/executive/summary");
    if (res?.ok) checkSessionDrift(res.data?.customer_id);
    return res;
  },
  chat: async (message, sessionId) => {
    try {
      const body = { message, session_id: sessionId };
      const res = await client.post("/executive/chat", body, { timeout: 30000 });
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err, "Sorry, I could not process that right now. Please try again in a moment.") };
    }
  },
  ceoChat:      (m, s) => chatCall("/ceo/chat", m, s),
  ceoInsights:  () =>     getCall("/ceo/insights"),
  cmoChat:      (m, s) => chatCall("/cmo/chat", m, s),
  cmoInsights:  () =>     getCall("/cmo/insights"),
  /** CMO Universal Daily Marketing Operating System (Phase 1) — deterministic
   * daily marketing tasks, product/segment/country/calendar-aware, no LLM. */
  cmoDailyTasks: () => getCall("/cmo/daily-tasks"),
  ctoChat:      (m, s) => chatCall("/cto/chat", m, s),
  ctoInsights:  () =>     getCall("/cto/insights"),
  projectileChat:    (m, s) => chatCall("/projectile/chat", m, s),
  projectileInsights:() =>     getCall("/projectile/insights"),
  forecastChat:      (m, s) => chatCall("/forecast/chat", m, s),
  forecastInsights:  () =>     getCall("/forecast/insights"),
  alertsChat:        (m, s) => chatCall("/alerts/chat", m, s),
  alertsInsights:    () =>     getCall("/alerts/insights"),
  benchmarkChat:     (m, s) => chatCall("/benchmark/chat", m, s),
  benchmarkInsights: () =>     getCall("/benchmark/insights"),

  /** Benchmarking Phase 2 — Competitive Landscape (Industry Benchmark /
   * Comparable Competitors / Relevant Industry Leaders). Reads the latest
   * PERSISTED snapshot — zero LLM calls, safe to call on every page load.
   * A refresh only happens via `benchmarkRefreshSnapshot` (deliberate
   * founder action, 30-min cooldown enforced server-side). */
  benchmarkCompetitiveSnapshot: (customerId, productId) =>
    get(`/benchmark/competitive-snapshot?customer_id=${encodeURIComponent(customerId)}${productId ? `&product_id=${encodeURIComponent(productId)}` : ""}`),
  benchmarkRefreshSnapshot: async (customerId, productId) => {
    try {
      const res = await client.post(
        "/benchmark/competitive-snapshot/refresh",
        { customer_id: customerId, ...(productId ? { product_id: productId } : {}) },
        { timeout: 15000 },
      );
      return { ok: true, data: res.data };
    } catch (err) {
      if (err.response?.status === 429) {
        const detail = err.response.data?.detail || {};
        return { ok: false, cooldown: true, retryAfterS: detail.retry_after_s, error: detail.message };
      }
      return { ok: false, error: friendly(err) };
    }
  },
  benchmarkSnapshotJobStatus: (jobId) =>
    get(`/benchmark/competitive-snapshot/status/${encodeURIComponent(jobId)}`),
  benchmarkAddCompetitor: async (customerId, companyName) => {
    try {
      const res = await client.post(
        "/benchmark/competitors/add", { customer_id: customerId, company_name: companyName }, { timeout: 15000 },
      );
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },
  benchmarkSetCompetitorStatus: async (competitorId, status) => {
    try {
      const res = await client.post(
        `/benchmark/competitors/${encodeURIComponent(competitorId)}/status`, { status }, { timeout: 15000 },
      );
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },

  /** Product-Driven Competitor Discovery (2026-02) — the founder's real
   * Product / Market Segment / Country. Its mere existence switches
   * discovery from generic industry-based to product-driven on the NEXT
   * refresh; no separate "confirm" step. */
  benchmarkGetProfile: (customerId) =>
    get(`/benchmark/profile?customer_id=${encodeURIComponent(customerId)}`),
  benchmarkSaveProfile: async (customerId, { primaryProduct, marketSegment, country }) => {
    try {
      const res = await client.post("/benchmark/profile", {
        customer_id: customerId, primary_product: primaryProduct,
        market_segment: marketSegment, country,
      }, { timeout: 15000 });
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },
  /** Founder review of a Suggested Direct Competitor — decision is one of
   * "accept" | "ignore" | "archive". */
  benchmarkReviewCompetitor: async (competitorId, decision) => {
    try {
      const res = await client.post(
        `/benchmark/competitors/${encodeURIComponent(competitorId)}/review`, { decision }, { timeout: 15000 },
      );
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },

  /** Multi-Product Benchmark Profiles (2026-11) — up to 10 active
   * products/company, each independently researched for competitor
   * intelligence. `benchmarkGetProfile`/`benchmarkSaveProfile` above stay
   * as-is for backward compatibility (they always read/write the primary
   * product); these are the new per-product CRUD calls. */
  benchmarkListProducts: (customerId) =>
    get(`/benchmark/products?customer_id=${encodeURIComponent(customerId)}`),
  benchmarkAddProduct: async (customerId, product) => {
    try {
      const res = await client.post(
        `/benchmark/products?customer_id=${encodeURIComponent(customerId)}`, product, { timeout: 15000 },
      );
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },
  benchmarkUpdateProduct: async (customerId, productId, product) => {
    try {
      const res = await client.put(
        `/benchmark/products/${encodeURIComponent(productId)}?customer_id=${encodeURIComponent(customerId)}`,
        product, { timeout: 15000 },
      );
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },
  benchmarkRemoveProduct: async (customerId, productId) => {
    try {
      const res = await client.delete(
        `/benchmark/products/${encodeURIComponent(productId)}?customer_id=${encodeURIComponent(customerId)}`,
        { timeout: 15000 },
      );
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },
  /** Section 6 — converts an approved competitive differentiator/
   * opportunity finding into a Command Center task (never a Tally-vs-peer
   * gap finding — the backend rejects those with a 400). */
  benchmarkCreateTaskFromFinding: async (customerId, findingId) => {
    try {
      const res = await client.post(
        `/benchmark/findings/${encodeURIComponent(findingId)}/create-task`,
        { customer_id: customerId }, { timeout: 15000 },
      );
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },
  caportalChat:      (m, s) => chatCall("/caportal/chat", m, s),
  caportalInsights:  () =>     getCall("/caportal/insights"),
  advancedAiChat:    (m, s) => chatCall("/advancedai/chat", m, s),
  advancedAiInsights:() =>     getCall("/advancedai/insights"),

  /** Direction Layer — deployed strategy (Phase 2). */
  activeStrategy: () => {
    const cid = activeCustomerId();
    if (!cid) return Promise.resolve({ ok: false, error: "Not logged in" });
    return get(`/intelligence/strategy/active?customer_id=${encodeURIComponent(cid)}`);
  },

  /** Canonical Strategic Target (Fix Pack 1C) — ONE persisted target per
   * customer, decoupled from any generated/deployed strategy. Response
   * now also includes `gap` (P0 architecture redesign, 2026-08) — the
   * deterministic TRAJECTORY derived FROM the target (current run-rate ->
   * target over its horizon), recomputed fresh on every read, never
   * itself a target. */
  strategicTarget: () => {
    const cid = activeCustomerId();
    if (!cid) return Promise.resolve({ ok: false, error: "Not logged in" });
    // Custom (longer) timeout: under concurrent page-load (e.g. Company
    // Trajectory firing several requests at once) the generic 12s client
    // default was intermittently swallowing this response, making an
    // already-deployed target flash as "Not set".
    return get(`/intelligence/strategic-target?customer_id=${encodeURIComponent(cid)}`, 20000);
  },

  /** Set/edit the canonical Strategic Target directly — no plan
   * generation required (P0 architecture redesign, 2026-08). `horizonYears`
   * is a free positive float, never restricted to 1/3/5/10. */
  saveStrategicTarget: async ({ targetValueCr, horizonYears, focus }) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post(`/intelligence/strategic-target`, {
        customer_id: cid,
        target_value: Number(targetValueCr),
        horizon_years: Number(horizonYears),
        focus: focus || null,
      }, { timeout: 15000 });
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },

  /** Scoped, honest Product Context for Company Trajectory (section 10) —
   * primary product + inventory-connection signal only, never fabricated
   * SKU-level economics. */
  productContext: () => {
    const cid = activeCustomerId();
    if (!cid) return Promise.resolve({ ok: false, error: "Not logged in" });
    return get(`/intelligence/product-context?customer_id=${encodeURIComponent(cid)}`);
  },

  /** GST Compliance Status (CFO module) — own-GSTIN registration + filing
   * status, reported straight from GSTN via the existing Supplier Audit
   * GST service. Status only, no amounts. */
  gstComplianceStatus: (customerId) =>
    get(`/gst-compliance/status/${encodeURIComponent(customerId)}`),

  /** Command Center — unified cross-module Decision Queue (Phase 2).
   * `useLlm=false` (now the default caller pattern) skips the server-side
   * LLM briefing call — the single biggest latency line-item (~3s) on a
   * screen whose default tab never renders it. Company Trajectory also
   * uses this with `useLlm=false` — it only needs
   * `analytical.target_pacing`, the SAME shared pacing calculation
   * Command Center uses (never a second formula, see Trajectory
   * Milestones, 2026-08). See `commandCenterBriefing` for the deferred,
   * background-loaded narrative. */
  commandCenter: async ({ useLlm = true } = {}) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.get(
        `/executive-intelligence/command-center?customer_id=${encodeURIComponent(cid)}&use_llm=${useLlm ? 1 : 0}`,
        { timeout: useLlm ? 35000 : 15000 },
      );
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },
  /** Fetches ONLY the LLM narrative from data Command Center already
   * returned (`pulse`/`decisions`) — no re-fetch of metrics/strategy/
   * target. Called in the background after the fast `commandCenter()`
   * load so the founder never waits on it (2026-08 latency audit). */
  commandCenterBriefing: async ({ pulse, decisions }) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post("/executive-intelligence/command-center/briefing",
        { customer_id: cid, pulse, decisions: decisions || [] }, { timeout: 20000 });
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },
  /** Approve / dismiss / act on a queued decision. action: accept|dismiss|act */
  commandCenterAct: async ({ action, ruleId, decisionId, title, role, reason }) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      await client.post("/executive-intelligence/feedback", {
        customer_id: cid, action, rule_id: ruleId,
        recommendation_id: decisionId, title, role, reason,
      }, { timeout: 20000 });
      return { ok: true };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },

  /**
   * Approve (act) or dismiss a decision and trigger the Business-OS chain
   * reaction. Returns the refreshed Command Center payload plus, for `act`,
   * a `chain_reaction` narrative (memory → propagation → recalc → trajectory
   * → expected improvement → next recommendation).
   */
  commandCenterActChain: async ({ action, ruleId, decisionId, title, role, reason }) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post("/executive-intelligence/command-center/act", {
        customer_id: cid, action, rule_id: ruleId,
        decision_id: decisionId, title, role, reason,
      }, { timeout: 60000 });
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },

  /**
   * "Assign Task via WhatsApp" — best-effort activity log only ("Task
   * shared via WhatsApp"). Fire-and-forget: the actual WhatsApp deep-link
   * has already opened client-side before this call is made, so a
   * failure here never blocks or breaks the share flow.
   */
  commandCenterTaskShared: async ({ ruleId, decisionId, title }) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false };
    try {
      await client.post("/executive-intelligence/command-center/task-shared", {
        customer_id: cid, rule_id: ruleId, decision_id: decisionId, title, channel: "whatsapp",
      }, { timeout: 8000 });
      return { ok: true };
    } catch (_err) {
      return { ok: false };
    }
  },

  /**
   * Phase 5 — Campaign & Content Playbooks. `build` is idempotent: returns
   * the existing playbook for this task's campaign if one exists, else
   * creates it (never a duplicate — see cmo_rules.campaign_key_for_task_key).
   */
  cmoPlaybookBuild: async (ruleId) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post("/cmo/playbook/build", { customer_id: cid, rule_id: ruleId }, { timeout: 20000 });
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },
  cmoPlaybookGet: async (ruleId) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.get(`/cmo/playbook`, { params: { customer_id: cid, rule_id: ruleId }, timeout: 15000 });
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },
  cmoPlaybookPatch: async (playbookId, patch) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.patch(`/cmo/playbook/${playbookId}`, { customer_id: cid, ...patch }, { timeout: 15000 });
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },
  cmoPlaybookList: async (limit = 5) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.get(`/cmo/playbook/list`, { params: { customer_id: cid, limit }, timeout: 15000 });
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },
  cmoPlaybookGenerateContent: async (playbookId, contentType, channel, variants = 3) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post(`/cmo/playbook/${playbookId}/generate-content`,
        { customer_id: cid, content_type: contentType, channel, variants }, { timeout: 30000 });
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },

  /**
   * Customer-specific AI insights powered by the calculation engine.
   * Falls back to the per-module demo endpoint when no customer is logged in.
   *
   * @param {string} module — cfo | ceo | cmo | cto | inventory | intelligence | alerts
   * @param {function} demoFetchFn — used for visitors (e.g., api.alertsInsights)
   * @param {boolean} force — true for an explicit "Regenerate" click; bypasses
   *   the backend's saved-insight cache. Omit/false for a normal page load.
   */
  aiInsightsGenerate: async (module, demoFetchFn, force = false) => {
    const cid = activeCustomerId();
    if (!cid) return demoFetchFn ? demoFetchFn() : { ok: false, error: "Not logged in" };
    try {
      // All calls now return quickly (cached path <5 s; LLM runs in the
      // background). 15 s is enough for any gateway limit.
      const res = await client.post(
        "/ai-insights/generate",
        { module, force: !!force },
        { timeout: 15000 }
      );
      // The legacy InsightsGrid expects { items, source }. Normalise our payload.
      const items = (res.data?.items || []).map((it) => ({
        id:       it.id,
        severity: it.severity === "high" ? "red" : it.severity === "low" ? "green" : "amber",
        title:    it.title,
        body:     it.body,
        action:   it.action,
      }));
      return { ok: true, data: { items, source: (res.data?.source === "gemini" || res.data?.source === "ai") ? "ai" : (res.data?.source || "ai"), generating: res.data?.generating || false, confidence: res.data?.confidence, data_sources: res.data?.data_sources } };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },

  // ---- C-ORE Connect (WhatsApp alerts) ----
  connectGetSettings: async () => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    return get(`/connect/settings?customer_id=${encodeURIComponent(cid)}`);
  },
  connectSaveSettings: async (settings) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.put("/connect/settings", { customer_id: cid, ...settings }, { timeout: 15000 });
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },
  connectPreview: async () => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    return get(`/connect/preview?customer_id=${encodeURIComponent(cid)}`);
  },
  connectTestSend: async (kind = "brief") => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post("/connect/test-send", { customer_id: cid, kind }, { timeout: 30000 });
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },

  // ---- C-ORE Fund (funding advisory) ----
  fundStatus: async () => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    return get(`/fund/status?customer_id=${encodeURIComponent(cid)}`);
  },
  fundApply: async (consent) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post("/fund/apply", { customer_id: cid, consent: !!consent }, { timeout: 20000 });
      return { ok: true, data: res.data };
    } catch (err) {
      return { ok: false, error: friendly(err) };
    }
  },

  // ---- C-ORE Plans & Billing (Razorpay) ----
  billingPlans: () => get("/billing/plans"),
  billingMe: async () => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    return get(`/billing/me?customer_id=${encodeURIComponent(cid)}`);
  },
  billingHistory: async () => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    return get(`/billing/history?customer_id=${encodeURIComponent(cid)}`);
  },
  billingValidateCoupon: async (planCode, couponCode) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post("/billing/coupons/validate",
        { customer_id: cid, plan_code: planCode, coupon_code: couponCode }, { timeout: 15000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err), data: err.response?.data }; }
  },
  billingTrialStart: async () => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post("/billing/trial/start", { customer_id: cid }, { timeout: 20000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err), data: err.response?.data }; }
  },
  billingSubscriptionCreate: async (planCode, couponCode) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post("/billing/subscriptions/create",
        { customer_id: cid, plan_code: planCode, coupon_code: couponCode || null }, { timeout: 30000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err), data: err.response?.data }; }
  },
  billingSubscriptionVerify: async (payload) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post("/billing/subscriptions/verify",
        { customer_id: cid, ...payload }, { timeout: 30000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err), data: err.response?.data }; }
  },
  billingSubscriptionCancel: async () => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post("/billing/subscriptions/cancel", { customer_id: cid }, { timeout: 30000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err), data: err.response?.data }; }
  },
  billingOrderCreate: async (planCode, couponCode) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post("/billing/orders/create",
        { customer_id: cid, plan_code: planCode || "lifetime", coupon_code: couponCode || null }, { timeout: 30000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err), data: err.response?.data }; }
  },
  billingPaymentVerify: async (payload) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post("/billing/payments/verify",
        { customer_id: cid, ...payload }, { timeout: 30000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err), data: err.response?.data }; }
  },

  // ---- C-ORE Task + Playbook + WhatsApp Execution System (2026-08) ----
  /** Mints (or reuses) the secure, no-login share-link token for a task. */
  assignLinkMint: async (ruleId) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post("/executive-intelligence/command-center/assign-link",
        { customer_id: cid, rule_id: ruleId }, { timeout: 15000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err) }; }
  },
  /** Founder creates a standalone task — no Playbook. */
  createFounderTask: async ({ title, description, priority, dueDate }) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post("/tasks/create",
        { customer_id: cid, title, description, priority, due_date: dueDate || null }, { timeout: 15000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err) }; }
  },
  /** Founder creates a task + a Playbook in one session. */
  createFounderTaskWithPlaybook: async ({ title, description, priority, dueDate, playbook }) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post("/tasks/create-with-playbook",
        { customer_id: cid, title, description, priority, due_date: dueDate || null, playbook }, { timeout: 15000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err) }; }
  },

  // ---- C-ORE Notification Center (2026-08) — Overdue Task alerts ----
  listNotifications: async () => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.get(`/notifications?customer_id=${encodeURIComponent(cid)}`, { timeout: 15000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err) }; }
  },
  markNotificationRead: async (notificationId) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post(`/notifications/${notificationId}/read`, { customer_id: cid }, { timeout: 10000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err) }; }
  },
  markAllNotificationsRead: async () => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post("/notifications/read-all", { customer_id: cid }, { timeout: 10000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err) }; }
  },

  // ---- Public "shared task" no-login view — never sends customer_id ----
  sharedGetTask: async (token) => {
    try {
      const res = await client.get(`/shared/task/${token}`, { timeout: 15000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err), status: err.response?.status }; }
  },
  sharedSetName: async (token, name) => {
    try {
      const res = await client.post(`/shared/task/${token}/name`, { name }, { timeout: 10000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err) }; }
  },
  sharedToggleChecklist: async (token, index, done) => {
    try {
      const res = await client.post(`/shared/task/${token}/checklist`, { index, done }, { timeout: 10000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err) }; }
  },
  sharedCompleteTask: async (token, name) => {
    try {
      const res = await client.post(`/shared/task/${token}/complete`, { name }, { timeout: 15000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err), status: err.response?.status }; }
  },

  // ---- C-ORE Expert — human advisory layer ----
  expertAccess: async () => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.get(`/expert/access?customer_id=${cid}`, { timeout: 12000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err) }; }
  },
  expertRequests: async () => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.get(`/expert/requests?customer_id=${cid}`, { timeout: 12000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err), status: err.response?.status }; }
  },
  expertCreateRequest: async ({ category, requestText, desiredOutcome }) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post("/expert/requests", {
        customer_id: cid, category, request_text: requestText, desired_outcome: desiredOutcome || null,
      }, { timeout: 15000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err), status: err.response?.status }; }
  },
  expertShareUpdate: async (requestId, note) => {
    const cid = activeCustomerId();
    if (!cid) return { ok: false, error: "Not logged in" };
    try {
      const res = await client.post(`/expert/requests/${requestId}/updates`,
        { customer_id: cid, note }, { timeout: 12000 });
      return { ok: true, data: res.data };
    } catch (err) { return { ok: false, error: friendly(err) }; }
  },
};
